import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, LOGO_FULL } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, ShieldCheck, FileText, Clock, XCircle, KeyRound, LogIn } from "lucide-react";
import axios from "axios";

export default function Onboard() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [initials, setInitials] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [signedNow, setSignedNow] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [loginReady, setLoginReady] = useState(false);

  const base = `${API}/onboard/${token}`;

  const load = () => axios.get(base)
    .then((r) => { setData(r.data); setName(r.data.name || ""); })
    .catch((e) => setError(e.response?.data?.detail || "This link is invalid or has expired."));

  useEffect(() => { load(); }, [token]);

  const submit = async () => {
    if (!sig || !name.trim() || !initials.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${base}/sign`, { signature: sig, printed_name: name, initials });
      setSignedNow(true);
      await load();
    } catch (e) { setError(e.response?.data?.detail || "Could not submit acknowledgement."); }
    finally { setBusy(false); }
  };

  const setPassword = async () => {
    setPwErr("");
    if (pw.length < 8) { setPwErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setPwErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await axios.post(`${base}/set-password`, { password: pw });
      setLoginReady(true);
    } catch (e) { setPwErr(e.response?.data?.detail || "Could not create your login."); }
    finally { setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-navy font-heading font-semibold text-lg">Unable to open agreement</p>
        <p className="text-slate-500 text-sm mt-2">{error}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-navy flex items-center justify-center text-white">Loading…</div>;

  const done = signedNow || data.already_signed;
  const needsPassword = done && !data.has_login && !loginReady;

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-navy px-5 py-5 flex flex-col items-center">
        <img src={LOGO_FULL} alt="True Seal Caulking" className="h-16 object-contain" />
      </header>

      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-4 pb-16">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h1 className="font-heading font-bold text-navy text-xl">Contractor Agreement — Acknowledgement</h1>
          <p className="text-slate-500 text-sm mt-1">Welcome, {data.name}. Please review your agreement and agreed rates below.</p>
          {data.agreement_filename && (
            <a href={`${base}/agreement`} target="_blank" rel="noreferrer" data-testid="onboard-view-agreement" className="inline-flex items-center gap-1 text-blue-700 text-sm font-medium mt-3"><FileText className="w-4 h-4" />View Contractor Agreement (PDF)</a>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3">Agreed Rates</h4>
          <div className="divide-y divide-slate-100">
            {data.rates.map(([k, v], i) => (
              <div key={i} className="flex items-center justify-between gap-4 py-2 text-sm">
                <span className="text-slate-600">{k}</span>
                <span className="text-navy font-medium">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {loginReady ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-heading font-bold text-navy text-lg">Your login is ready</h2>
            <p className="text-slate-500 text-sm mt-2">Sign in with your email and the password you just set to complete your onboarding.</p>
            <Button data-testid="onboard-goto-login" onClick={() => navigate("/login")} className="mt-5 bg-navy hover:bg-navy-light py-6 px-8 text-base"><LogIn className="w-5 h-5 mr-2" />Go to Login</Button>
          </div>
        ) : needsPassword ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><KeyRound className="w-5 h-5" />Create your login</h3>
            <p className="text-sm text-slate-500 mt-2 mb-4">Set a password to sign in and complete the rest of your onboarding (profile & documents).</p>
            <label className="text-xs uppercase tracking-wider text-slate-400">Password</label>
            <Input data-testid="onboard-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 mb-4" placeholder="At least 8 characters" />
            <label className="text-xs uppercase tracking-wider text-slate-400">Confirm Password</label>
            <Input data-testid="onboard-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1 mb-2" placeholder="Re-enter password" />
            {pwErr && <p className="text-sm text-red-600 mb-2" data-testid="onboard-password-error">{pwErr}</p>}
            <Button data-testid="onboard-setpassword-button" onClick={setPassword} disabled={busy || !pw || !pw2} className="w-full mt-3 bg-navy hover:bg-navy-light py-6 text-base"><KeyRound className="w-5 h-5 mr-2" />{busy ? "Creating…" : "Create Login & Continue"}</Button>
          </div>
        ) : done ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-heading font-bold text-navy text-lg">Agreement Acknowledged</h2>
            <p className="text-slate-500 text-sm mt-2">You've already created your login. Please sign in to continue your onboarding.</p>
            <Button data-testid="onboard-goto-login" onClick={() => navigate("/login")} className="mt-5 bg-navy hover:bg-navy-light py-6 px-8 text-base"><LogIn className="w-5 h-5 mr-2" />Go to Login</Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><ShieldCheck className="w-5 h-5" />Declaration</h3>
            <p className="text-sm text-slate-500 mt-2 mb-4">"{data.declaration}"</p>
            <label className="text-xs uppercase tracking-wider text-slate-400">Printed Name</label>
            <Input data-testid="onboard-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 mb-4" placeholder="Your full name" />
            <label className="text-xs uppercase tracking-wider text-slate-400">Initials</label>
            <Input data-testid="onboard-initials-input" value={initials} onChange={(e) => setInitials(e.target.value)} className="mt-1 mb-4 max-w-[140px]" placeholder="e.g. J.S." />
            <label className="text-xs uppercase tracking-wider text-slate-400">Signature</label>
            <div className="mt-1">
              {sig ? (
                <div className="border border-slate-200 rounded-xl p-3">
                  <img src={sig} alt="signature" className="h-20 object-contain" />
                  <button onClick={() => setSig("")} className="text-xs text-blue-700 mt-2">Redo signature</button>
                </div>
              ) : <SignaturePad onSave={setSig} saving={false} />}
            </div>
            <Button data-testid="onboard-submit-button" onClick={submit} disabled={busy || !sig || !name.trim() || !initials.trim()} className="w-full mt-4 bg-navy hover:bg-navy-light py-6 text-base">
              <CheckCircle2 className="w-5 h-5 mr-2" />{busy ? "Submitting…" : "Acknowledge & Sign"}
            </Button>
            <p className="text-[11px] text-slate-400 text-center mt-3 flex items-center justify-center gap-1"><Clock className="w-3 h-3" />Secure electronic signature</p>
          </div>
        )}
      </div>
    </div>
  );
}
